import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/layouts/MainLayout';
import { Button, Alert, PageHeader, Container } from '@/components/ui';
import { DeleteConfirmModal } from '@/components/ui';
import { useUserGroupsStore } from '@/store/userGroupsStore';
import { useNotificationStore } from '@/store/notificationStore';
import { USER_ROLE_ORDER } from '@/constants/userGroups.constants';
import {
  UserGroupDialog,
  UserGroupHeading,
  UserGroupRightsItem,
  MobileRoleCard,
} from '@/components/userGroups';
import { UserGroupService } from '@/services/userGroups.service';
import type { UserGroup } from '@/types/userGroups.types';

/**
 * UserGroupsPage - Main component for managing user groups
 */
export const UserGroupsPage: React.FC = () => {
  const { userGroups, groupRoles, isLoading, error, fetchUserGroupsAndRoles, clearError } =
    useUserGroupsStore();

  const { addNotification } = useNotificationStore();

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUserGroup, setEditingUserGroup] = useState<UserGroup | undefined>(undefined);

  // Mobile: accordion open sections (index-based, all open by default)
  const [openSections, setOpenSections] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    USER_ROLE_ORDER.forEach((_, i) => initial.add(i));
    return initial;
  });

  // Mobile: group chip menu
  const [mobileOpenMenuId, setMobileOpenMenuId] = useState<number | null>(null);

  // Mobile: delete confirmation
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState<UserGroup | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);

  useEffect(() => {
    fetchUserGroupsAndRoles().catch((err) => {
      console.error('Failed to fetch user groups and roles:', err);
    });
  }, [fetchUserGroupsAndRoles]);

  const handleCreateUserGroup = () => {
    setEditingUserGroup(undefined);
    setIsDialogOpen(true);
  };

  const handleEditUserGroup = (userGroup: UserGroup) => {
    setEditingUserGroup(userGroup);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingUserGroup(undefined);
  };

  const refreshUserGroups = async () => {
    try {
      await fetchUserGroupsAndRoles();
    } catch (err) {
      console.error('Failed to refresh user groups:', err);
    }
  };

  const handleUserGroupSuccess = (operation: 'created' | 'updated', userGroupName: string) => {
    addNotification({
      type: 'success',
      title: 'User Group',
      message: `User group "${userGroupName}" has been successfully ${operation}.`,
    });
    setIsDialogOpen(false);
    setEditingUserGroup(undefined);
    refreshUserGroups();
  };

  const handleDeleteSuccess = (userGroupName: string) => {
    addNotification({
      type: 'success',
      title: 'User Group',
      message: `User group "${userGroupName}" has been successfully deleted.`,
    });
    refreshUserGroups();
  };

  const handleRightsUpdateSuccess = (roleName: string) => {
    addNotification({
      type: 'success',
      title: 'Rights Updated',
      message: `User rights "${roleName}" have been successfully updated.`,
    });
    refreshUserGroups();
  };

  const handleClearError = () => {
    clearError();
  };

  const handleUserGroupReorderSuccess = () => {
    addNotification({
      type: 'success',
      title: 'Order Updated',
      message: 'User group order has been updated.',
    });
    refreshUserGroups();
  };

  // Mobile: toggle accordion section
  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Mobile: request delete confirmation
  const handleMobileRequestDelete = (userGroup: UserGroup) => {
    setMobileOpenMenuId(null);
    setPendingDeleteGroup(userGroup);
  };

  // Mobile: confirm delete
  const handleMobileDeleteConfirm = async () => {
    if (!pendingDeleteGroup) return;
    setIsDeletingGroup(true);
    try {
      await UserGroupService.deleteUserGroup(pendingDeleteGroup.id);
      handleDeleteSuccess(pendingDeleteGroup.name);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      addNotification({ type: 'error', title: 'Delete Failed', message: errorMessage });
    } finally {
      setIsDeletingGroup(false);
      setPendingDeleteGroup(null);
    }
  };

  // Render desktop skeleton loading state
  const renderSkeletonLoader = () => (
    <div className="space-y-2">
      {USER_ROLE_ORDER.map((item, groupIndex) => (
        <div key={groupIndex} className="space-y-2">
          {item.label && (
            <div className="sticky left-0 mb-2 mt-4 pl-2 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {item.label}
            </div>
          )}
          {item.roleCode.map((roleCode, roleIndex) => (
            <div
              key={`${roleCode}-${roleIndex}`}
              className="h-14 w-full animate-pulse rounded-lg bg-gray-100 dark:bg-dark-700"
            />
          ))}
        </div>
      ))}
    </div>
  );

  // Render mobile skeleton loading state
  const renderMobileSkeletonLoader = () => (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-dark-600"
        >
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-dark-700" />
          {[1, 2, 3].map((j) => (
            <div key={j} className="flex items-center justify-between py-2">
              <div className="h-3 w-28 animate-pulse rounded bg-gray-100 dark:bg-dark-700" />
              <div className="h-6 w-11 animate-pulse rounded-full bg-gray-100 dark:bg-dark-700" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <MainLayout>
      <Container>
        <div className="space-y-6">
          {/* Page Header */}
          <PageHeader
            title="User Group Management"
            actions={
              <Button
                onClick={handleCreateUserGroup}
                title="Create New Group"
                leftIcon={
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                }
              >
                Create Group
              </Button>
            }
          />

          {/* Error Alert */}
          {error && (
            <Alert variant="error" onClose={handleClearError}>
              {error}
            </Alert>
          )}

          {/* ── DESKTOP TABLE LAYOUT (md and up) ─────────────────────────── */}
          <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-600 dark:bg-dark-900 md:block">
            <div className="relative">
              {/* Right-edge fade hint on sticky-left column — signals horizontally scrollable content */}
              <div className="pointer-events-none absolute inset-y-0 left-[280px] z-20 w-8 border-l border-gray-200 bg-gradient-to-r from-gray-200/70 to-white/0 dark:border-dark-600 dark:from-dark-900/55 dark:to-dark-900/0" />
              <div className="overflow-x-auto">
                <div className="min-w-max">
                  <UserGroupHeading
                    userGroups={userGroups}
                    isLoading={isLoading}
                    onDelete={handleDeleteSuccess}
                    onEdit={handleEditUserGroup}
                    onReorderSuccess={handleUserGroupReorderSuccess}
                  />

                  <div>
                    {!isLoading ? (
                      <div className="space-y-0">
                        {USER_ROLE_ORDER.map((item, groupIndex) => (
                          <div key={groupIndex}>
                            {item.label && (
                              <div className="mt-3 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-dark-800">
                                {/* Outer div is block-level — its width is driven by the widest row in the
                            card (heading/content rows), so the background + border span the full
                            scrollable width automatically. Only the label text needs sticky. */}
                                <div className="sticky left-0 z-[5] inline-flex items-center border-l-4 border-l-blue-500 bg-gray-50 px-4 py-2 dark:border-l-blue-400 dark:bg-dark-800">
                                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    {item.label}
                                  </span>
                                </div>
                              </div>
                            )}
                            {item.roleCode.map((roleCode) => {
                              const role = groupRoles[roleCode];
                              if (!role) return null;
                              return (
                                <UserGroupRightsItem
                                  key={roleCode}
                                  groupRole={role}
                                  userGroups={userGroups}
                                  onUpdateUserGroupRoles={handleRightsUpdateSuccess}
                                />
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4">{renderSkeletonLoader()}</div>
                    )}

                    {!isLoading && userGroups.length === 0 && !error && (
                      <div className="py-16 text-center">
                        <div className="mb-4 flex justify-center text-gray-400 dark:text-gray-600">
                          <svg
                            className="h-16 w-16"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                          </svg>
                        </div>
                        <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                          No User Groups Found
                        </h3>
                        <p className="mb-6 text-gray-500 dark:text-gray-400">
                          Get started by creating your first user group.
                        </p>
                        <Button onClick={handleCreateUserGroup}>Create Your First Group</Button>
                      </div>
                    )}
                  </div>
                </div>
                {/* close min-w-max */}
              </div>
              {/* close overflow-x-auto */}
            </div>
            {/* close relative */}
          </div>

          {/* ── MOBILE ACCORDION LAYOUT (below md) ───────────────────────── */}
          <div className="space-y-4 md:hidden">
            {/* Groups management strip */}
            {!isLoading && (
              <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-600 dark:bg-dark-900">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Groups
                </p>
                {userGroups.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No groups yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {userGroups.map((group) => (
                      <div key={group.id} className="relative">
                        <div
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium ${
                            group.isActive
                              ? 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'border-gray-200 bg-gray-100 text-gray-500 dark:border-dark-600 dark:bg-dark-700 dark:text-gray-400'
                          }`}
                        >
                          <span>{group.name}</span>
                          {!group.isActive && (
                            <span className="rounded-full bg-gray-200 px-1 py-px text-[9px] uppercase dark:bg-dark-600">
                              Off
                            </span>
                          )}
                          <button
                            onClick={() =>
                              setMobileOpenMenuId(mobileOpenMenuId === group.id ? null : group.id)
                            }
                            className="ml-0.5 rounded-full p-0.5 text-current opacity-60 hover:opacity-100"
                            title="Group actions"
                          >
                            <svg
                              className="h-3.5 w-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                              />
                            </svg>
                          </button>
                        </div>

                        {mobileOpenMenuId === group.id && (
                          <>
                            <div
                              className="fixed inset-0 z-20"
                              onClick={() => setMobileOpenMenuId(null)}
                            />
                            <div className="absolute left-0 top-full z-30 mt-1 w-40 rounded-md border border-gray-200 bg-white shadow-lg dark:border-dark-600 dark:bg-dark-700">
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    setMobileOpenMenuId(null);
                                    handleEditUserGroup(group);
                                  }}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark-600"
                                >
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleMobileRequestDelete(group)}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-dark-600"
                                >
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Loading state */}
            {isLoading && renderMobileSkeletonLoader()}

            {/* Accordion sections */}
            {!isLoading && (
              <div className="space-y-3">
                {USER_ROLE_ORDER.map((item, groupIndex) => {
                  const roles = item.roleCode.map((rc) => groupRoles[rc]).filter(Boolean);

                  if (roles.length === 0) return null;

                  const isOpen = openSections.has(groupIndex);

                  // Unlabelled group — always visible, no accordion wrapper
                  if (!item.label) {
                    return (
                      <div key={groupIndex} className="space-y-2">
                        {roles.map((role) => (
                          <MobileRoleCard
                            key={role!.roleCode}
                            groupRole={role!}
                            userGroups={userGroups}
                            onUpdateUserGroupRoles={handleRightsUpdateSuccess}
                          />
                        ))}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={groupIndex}
                      className="overflow-hidden rounded-lg border border-gray-200 dark:border-dark-600"
                    >
                      {/* Accordion header */}
                      <button
                        onClick={() => toggleSection(groupIndex)}
                        className="flex w-full items-center justify-between border-l-4 border-l-blue-500 bg-gray-50 px-4 py-3 text-left dark:border-l-blue-400 dark:bg-dark-800"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                            {item.label}
                          </span>
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                            {roles.length}
                          </span>
                        </div>
                        <svg
                          className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 dark:text-gray-400 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Accordion content */}
                      {isOpen && (
                        <div className="space-y-2 bg-gray-50 p-3 dark:bg-dark-800/50">
                          {roles.map((role) => (
                            <MobileRoleCard
                              key={role!.roleCode}
                              groupRole={role!}
                              userGroups={userGroups}
                              onUpdateUserGroupRoles={handleRightsUpdateSuccess}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Mobile empty state */}
            {!isLoading && userGroups.length === 0 && !error && (
              <div className="rounded-lg border border-gray-200 py-16 text-center dark:border-dark-600">
                <div className="mb-4 flex justify-center text-gray-400 dark:text-gray-600">
                  <svg className="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                  No User Groups Found
                </h3>
                <p className="mb-6 text-gray-500 dark:text-gray-400">
                  Get started by creating your first user group.
                </p>
                <Button onClick={handleCreateUserGroup}>Create Your First Group</Button>
              </div>
            )}
          </div>

          {/* User Group Dialog (shared between desktop & mobile) */}
          <UserGroupDialog
            isOpen={isDialogOpen}
            onClose={handleDialogClose}
            userGroup={editingUserGroup}
            onSuccess={handleUserGroupSuccess}
          />

          {/* Mobile delete confirm modal */}
          <DeleteConfirmModal
            isOpen={!!pendingDeleteGroup}
            onClose={() => setPendingDeleteGroup(null)}
            onConfirm={handleMobileDeleteConfirm}
            loading={isDeletingGroup}
            title="Delete User Group"
            message="Are you sure you want to delete this user group? This action cannot be undone."
            itemName={pendingDeleteGroup?.name}
          />
        </div>
      </Container>
    </MainLayout>
  );
};
